import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, FileText, Lightbulb, ListChecks } from 'lucide-react';
import GuideAccountCTA from '../components/GuideAccountCTA';
import './ApplicationTimelinePage.css';

const topTips = [
  'Do not wait for the perfect idea. Get a first draft down so you have something real to improve.',
  'Draft the main essay during summer if you can. Senior year gets busy quickly.',
  'Make the essay about you, even if another person, place, event, or challenge starts the story.',
  'Spend more space on impact, growth, and reflection than on explaining what happened.',
  'Avoid comparison language like unlike most people or while others. Confidence is good; superiority is not.',
  'A normal story can work beautifully when it is specific, honest, and told in your voice.',
];

const sections = [
  {
    title: 'Start before senior year gets busy',
    items: [
      'Aim to have a rough Common App personal essay draft before school starts.',
      'Start supplements early because many schools ask similar themes: why major, why us, community, identity, activity, challenge, or contribution.',
      'Your first draft can be messy. The goal is to move the idea from your head onto the page.',
    ],
  },
  {
    title: 'Make the essay about you',
    items: [
      'If your essay begins with another person, experience, activity, or challenge, bring the focus back to what it reveals about you.',
      'The reader should understand your values, choices, perspective, curiosity, and growth.',
      'Do not let the setting or another person become more vivid than you are.',
    ],
  },
  {
    title: 'Focus on impact and growth',
    items: [
      'As a rule of thumb, most of the essay should be about how you changed, what you learned, or why the moment matters.',
      'Avoid spending too much space summarizing the situation before the reader gets to your reflection.',
      'Show the before, the turning point, and the after, even if the change is quiet.',
    ],
  },
  {
    title: 'Show, then tell',
    items: [
      'Use specific scenes, details, decisions, and small moments so the reader can see the story.',
      'Then explain why those moments matter. Showing without reflection can feel unfinished.',
      'Specific details usually beat big claims like I am resilient or I am hardworking.',
    ],
  },
  {
    title: 'Avoid bragging and comparison',
    items: [
      'You can write about achievement without sounding arrogant.',
      'Avoid framing yourself as better than classmates, teammates, family, or community members.',
      'Let your actions, humility, and reflection do the work.',
    ],
  },
  {
    title: 'Use structure on purpose',
    items: [
      'A full-circle ending can make an essay memorable: return to an opening image, phrase, object, or question with new meaning.',
      'Keep the voice conversational at first. Polish for clarity later.',
      'Avoid repeating the same topic across your main essay and every supplement.',
    ],
  },
];

const maiCollegesTips = [
  'Use My Info to collect activities, honors, values, goals, and experiences before drafting.',
  'Use the Essay Tracker dashboard template like a bookmarked essay tab: save each prompt, draft link, word limit, draft status, final status, and submitted status.',
  'Use Link Hub for your main Google Docs, Notion pages, Common App, or writing workspace links.',
  'Use Progress tasks for essay draft, feedback, revision, final proofread, and submission checks.',
];

const sources = [
  { label: 'Common App essay prompts', url: 'https://www.commonapp.org/apply/essay-prompts' },
  { label: 'Common App first-year guide', url: 'https://www.commonapp.org/apply/first-year-students/' },
];

function EssayGamePlanPage() {
  return (
    <main className="timeline-page">
      <div className="timeline-page__inner">
        <header className="timeline-page__hero">
          <span className="timeline-page__eyebrow">Essay Game Plan</span>
          <h1>Write earlier, sound more like yourself</h1>
          <p>
            Essays feel huge because they are personal, but they are still a piece of the whole application.
            The goal is not to find the most dramatic story. The goal is to tell a real story in a way that
            shows how you think, what you value, and how you have grown.
          </p>
        </header>

        <section className="timeline-page__intro" aria-label="Essay reminder">
          <div className="timeline-page__intro-icon">
            <FileText size={19} strokeWidth={1.8} />
          </div>
          <div>
            <h2>Get it out of your head</h2>
            <p>
              Your essay cannot get better until it exists. Start with a rough, honest draft before trying to
              make it impressive. You can revise structure, language, and clarity later.
            </p>
          </div>
        </section>

        <section className="timeline-page__note timeline-page__note--action" aria-label="Use MaiColleges for essay work">
          <span className="timeline-page__action-kicker">Use MaiColleges</span>
          <h2>Use the Essay Tracker dashboard</h2>
          <p>
            Create an Essay Tracker dashboard so every prompt, word limit, draft link, draft status, final
            review, and submitted status lives in one place. Then use Progress for final submission tasks
            once a college is on your final list.
          </p>
          <div className="timeline-page__action-row">
            <Link to="/dashboard" className="timeline-page__internal-link">Open Dashboards</Link>
            <Link to="/progress" className="timeline-page__internal-link timeline-page__internal-link--secondary">Open Progress</Link>
          </div>
        </section>

        <section className="timeline-page__tips" aria-labelledby="essay-tips-title">
          <div className="timeline-page__section-heading">
            <Lightbulb size={18} strokeWidth={1.8} />
            <h2 id="essay-tips-title">Top tips</h2>
          </div>
          <div className="timeline-page__tip-grid">
            {topTips.map((tip) => (
              <article className="timeline-tip" key={tip}>
                <ListChecks size={16} strokeWidth={1.9} />
                <p>{tip}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="timeline-page__timeline" aria-labelledby="essay-plan-title">
          <div className="timeline-page__section-heading">
            <ListChecks size={18} strokeWidth={1.8} />
            <h2 id="essay-plan-title">Essay plan</h2>
          </div>
          <div className="timeline-list">
            {sections.map((section) => (
              <article className="timeline-block" key={section.title}>
                <div className="timeline-block__marker" aria-hidden="true" />
                <div className="timeline-block__content">
                  <h3>{section.title}</h3>
                  <ul>
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="timeline-page__note" aria-label="Use MaiColleges for essays">
          <h2>Use MaiColleges for this</h2>
          <ul className="timeline-page__note-list">
            {maiCollegesTips.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </section>

        <section className="timeline-page__sources" aria-labelledby="essay-sources-title">
          <h2 id="essay-sources-title">Helpful official sources</h2>
          <div className="timeline-source-grid">
            {sources.map((source) => (
              <a href={source.url} target="_blank" rel="noreferrer" key={source.url}>
                <span>{source.label}</span>
                <ExternalLink size={14} strokeWidth={1.9} />
              </a>
            ))}
          </div>
        </section>

        <GuideAccountCTA title="Save essay links and research in a dashboard" />
      </div>
    </main>
  );
}

export default EssayGamePlanPage;
